import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Award, TrendingUp, Users, MapPin, Zap, ArrowLeft } from 'lucide-react';
import api from '../services/api';
import defaultGroupPic from '../assets/defaultGroupPic.jpg'

const ClubsLeaderboard = () => {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await api.get('/club-wars/leaderboard/clubs?limit=100');
      setLeaderboard(response.data.leaderboard);
    } catch (error) {
      console.error('Erreur lors du chargement du classement:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMedalColor = (position) => {
    if (position === 1) return 'text-yellow-400';
    if (position === 2) return 'text-slate-300';
    if (position === 3) return 'text-orange-400';
    return 'text-gray-500';
  };

  const getRankBadge = (position) => {
    if (position === 1) return { icon: '👑', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400' };
    if (position === 2) return { icon: '🥈', bg: 'bg-slate-500/10', border: 'border-slate-400/30', text: 'text-slate-300' };
    if (position === 3) return { icon: '🥉', bg: 'bg-orange-500/10', border: 'border-orange-400/30', text: 'text-orange-400' };
    return { icon: null, bg: 'bg-slate-700', border: 'border-slate-600', text: 'text-gray-400' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto"></div>
          <p className="text-gray-400 font-medium">Chargement du classement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 pb-20">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-slate-950/95 backdrop-blur-sm border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center space-x-1 sm:space-x-2 text-gray-400 hover:text-white transition"
            >
              <ArrowLeft size={18} className="sm:w-5 sm:h-5" />
              <span className="font-medium text-sm sm:text-base">Retour</span>
            </button>
            
            <div className="flex items-center space-x-1.5 sm:space-x-2">
              <Trophy size={20} className="text-indigo-500 sm:w-6 sm:h-6" />
              <h1 className="text-lg sm:text-2xl font-bold text-white">Classement</h1>
            </div>
            
            <div className="w-16 sm:w-20"></div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-6 sm:py-8 space-y-6 sm:space-y-8">
        {/* Podium Top 3 */}
        {leaderboard.length >= 3 && (
          <div className="mb-8 sm:mb-12">
            {/* Champion - 1ère place */}
            <div className="mb-4 sm:mb-6">
              <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border-2 border-yellow-500/30 rounded-xl sm:rounded-2xl p-4 sm:p-6 relative overflow-hidden">
                {/* Effet de brillance */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"></div>
                
                <div className="relative z-10 flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                  {/* Médaille et position */}
                  <div className="flex-shrink-0 text-center">
                    <div className="text-4xl sm:text-6xl mb-1 sm:mb-2">👑</div>
                    <div className="text-3xl sm:text-5xl font-black text-yellow-400">#1</div>
                  </div>

                  {/* Image du club */}
                  <div className="flex-shrink-0">
                    <img
                      src={leaderboard[0].club.imageClub || defaultGroupPic}
                      alt={leaderboard[0].club.nom}
                      className="w-20 h-20 sm:w-28 sm:h-28 rounded-xl sm:rounded-2xl object-cover border-4 border-yellow-400/50 shadow-xl shadow-yellow-500/20"
                    />
                  </div>

                  {/* Infos du club */}
                  <div className="flex-1 text-center sm:text-left w-full">
                    <div className="inline-block px-3 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-bold rounded-full mb-2">
                      CHAMPION
                    </div>
                    <h2 className="text-xl sm:text-3xl font-bold text-white mb-1 truncate px-2 sm:px-0">
                      {leaderboard[0].club.nom}
                    </h2>
                    <div className="flex items-center justify-center sm:justify-start space-x-2 text-gray-400 mb-3 sm:mb-4">
                      <MapPin size={14} />
                      <span className="text-sm truncate">{leaderboard[0].club.ville}</span>
                    </div>

                    {/* Stats principales */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
                      <div className="bg-slate-800/50 rounded-lg sm:rounded-xl p-2 sm:p-3 border border-slate-700">
                        <div className="text-xl sm:text-2xl font-black text-yellow-400">
                          {leaderboard[0].stats.points}
                        </div>
                        <div className="text-xs text-gray-400">Points</div>
                      </div>
                      <div className="bg-slate-800/50 rounded-lg sm:rounded-xl p-2 sm:p-3 border border-slate-700">
                        <div className="text-xl sm:text-2xl font-black text-green-400">
                          {leaderboard[0].stats.warsWon}
                        </div>
                        <div className="text-xs text-gray-400">Victoires</div>
                      </div>
                      <div className="bg-slate-800/50 rounded-lg sm:rounded-xl p-2 sm:p-3 border border-slate-700">
                        <div className="text-xl sm:text-2xl font-black text-red-400">
                          {leaderboard[0].stats.warsLost}
                        </div>
                        <div className="text-xs text-gray-400">Défaites</div>
                      </div>
                      <div className="bg-slate-800/50 rounded-lg sm:rounded-xl p-2 sm:p-3 border border-slate-700">
                        <div className="text-xl sm:text-2xl font-black text-indigo-400">
                          {leaderboard[0].stats.winRate}%
                        </div>
                        <div className="text-xs text-gray-400">Taux</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 2ème et 3ème place */}
            <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
              {/* 2ème place */}
              <div 
                onClick={() => navigate(`/club/${leaderboard[1].club._id}`)}
                className="bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl p-4 sm:p-5 cursor-pointer transition-all hover:border-slate-500 group"
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="flex-shrink-0 text-center">
                    <div className="text-3xl sm:text-4xl mb-1">🥈</div>
                    <div className="text-2xl sm:text-3xl font-black text-slate-300">#2</div>
                  </div>
                  
                  <img
                    src={leaderboard[1].club.imageClub || defaultGroupPic}
                    alt={leaderboard[1].club.nom}
                    className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg sm:rounded-xl object-cover border-2 border-slate-600 group-hover:border-slate-500"
                  />
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-bold text-white truncate mb-1">
                      {leaderboard[1].club.nom}
                    </h3>
                    <div className="flex items-center space-x-1 text-gray-400 text-xs sm:text-sm mb-2 sm:mb-3">
                      <MapPin size={12} />
                      <span className="truncate">{leaderboard[1].club.ville}</span>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-1.5 sm:gap-2 text-center">
                      <div>
                        <div className="text-base sm:text-lg font-bold text-slate-300">{leaderboard[1].stats.points}</div>
                        <div className="text-xs text-gray-500">pts</div>
                      </div>
                      <div>
                        <div className="text-base sm:text-lg font-bold text-green-400">{leaderboard[1].stats.warsWon}</div>
                        <div className="text-xs text-gray-500">W</div>
                      </div>
                      <div>
                        <div className="text-base sm:text-lg font-bold text-indigo-400">{leaderboard[1].stats.winRate}%</div>
                        <div className="text-xs text-gray-500">%</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 3ème place */}
              <div 
                onClick={() => navigate(`/club/${leaderboard[2].club._id}`)}
                className="bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl p-4 sm:p-5 cursor-pointer transition-all hover:border-slate-500 group"
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="flex-shrink-0 text-center">
                    <div className="text-3xl sm:text-4xl mb-1">🥉</div>
                    <div className="text-2xl sm:text-3xl font-black text-orange-400">#3</div>
                  </div>
                  
                  <img
                    src={leaderboard[2].club.imageClub || defaultGroupPic}
                    alt={leaderboard[2].club.nom}
                    className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg sm:rounded-xl object-cover border-2 border-slate-600 group-hover:border-slate-500"
                  />
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-bold text-white truncate mb-1">
                      {leaderboard[2].club.nom}
                    </h3>
                    <div className="flex items-center space-x-1 text-gray-400 text-xs sm:text-sm mb-2 sm:mb-3">
                      <MapPin size={12} />
                      <span className="truncate">{leaderboard[2].club.ville}</span>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-1.5 sm:gap-2 text-center">
                      <div>
                        <div className="text-base sm:text-lg font-bold text-orange-400">{leaderboard[2].stats.points}</div>
                        <div className="text-xs text-gray-500">pts</div>
                      </div>
                      <div>
                        <div className="text-base sm:text-lg font-bold text-green-400">{leaderboard[2].stats.warsWon}</div>
                        <div className="text-xs text-gray-500">W</div>
                      </div>
                      <div>
                        <div className="text-base sm:text-lg font-bold text-indigo-400">{leaderboard[2].stats.winRate}%</div>
                        <div className="text-xs text-gray-500">%</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Liste complète */}
        {leaderboard.length > 3 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between mb-3 sm:mb-4 px-1 sm:px-2">
              <h2 className="text-lg sm:text-xl font-bold text-white flex items-center space-x-1.5 sm:space-x-2">
                <TrendingUp size={18} className="text-indigo-500 sm:w-5 sm:h-5" />
                <span>Classement complet</span>
              </h2>
              <span className="text-xs sm:text-sm text-gray-500">{leaderboard.length} clubs</span>
            </div>

            {leaderboard.slice(3).map((entry, index) => {
              const position = index + 4;
              const badge = getRankBadge(position);

              return (
                <div
                  key={entry.club._id}
                  onClick={() => navigate(`/club/${entry.club._id}`)}
                  className="bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-600 rounded-xl p-3 sm:p-4 cursor-pointer transition-all group"
                >
                  <div className="flex items-center gap-3 sm:gap-4">
                    {/* Position */}
                    <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl ${badge.bg} border ${badge.border} flex items-center justify-center`}>
                      {badge.icon ? (
                        <span className="text-xl sm:text-2xl">{badge.icon}</span>
                      ) : (
                        <span className={`text-lg sm:text-xl font-black ${badge.text}`}>#{position}</span>
                      )}
                    </div>

                    {/* Image club */}
                    <img
                      src={entry.club.imageClub || defaultGroupPic}
                      alt={entry.club.nom}
                      className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl object-cover border-2 border-slate-600 group-hover:border-slate-500"
                    />

                    {/* Infos club */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-white truncate mb-0.5 sm:mb-1 text-sm sm:text-base">
                        {entry.club.nom}
                      </h3>
                      <div className="flex items-center space-x-2 sm:space-x-3 text-xs sm:text-sm text-gray-400">
                        <div className="flex items-center space-x-1">
                          <MapPin size={12} />
                          <span className="truncate max-w-[100px] sm:max-w-none">{entry.club.ville}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Users size={12} />
                          <span>{entry.club.nombreMembres}</span>
                        </div>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="hidden md:flex items-center gap-3 lg:gap-4">
                      <div className="text-center">
                        <div className="text-base lg:text-lg font-bold text-indigo-400">
                          {entry.stats.points}
                        </div>
                        <div className="text-xs text-gray-500">pts</div>
                      </div>
                      <div className="text-center">
                        <div className="text-base lg:text-lg font-bold text-green-400">
                          {entry.stats.warsWon}
                        </div>
                        <div className="text-xs text-gray-500">W</div>
                      </div>
                      <div className="text-center">
                        <div className="text-base lg:text-lg font-bold text-red-400">
                          {entry.stats.warsLost}
                        </div>
                        <div className="text-xs text-gray-500">L</div>
                      </div>
                      <div className="text-center">
                        <div className="text-base lg:text-lg font-bold text-blue-400">
                          {entry.stats.winRate}%
                        </div>
                        <div className="text-xs text-gray-500">Taux</div>
                      </div>
                    </div>

                    {/* Points en mobile */}
                    <div className="md:hidden text-right flex-shrink-0">
                      <div className="text-lg sm:text-xl font-bold text-indigo-400">
                        {entry.stats.points}
                      </div>
                      <div className="text-xs text-gray-500">points</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Info card */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 sm:p-6">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center">
              <Zap size={18} className="text-indigo-500 sm:w-5 sm:h-5" />
            </div>
            <div>
              <h3 className="text-white font-bold mb-2 text-sm sm:text-base">Comment ça marche ?</h3>
              <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-indigo-500 mt-0.5 flex-shrink-0">•</span>
                  <span>Les clubs gagnent des points en remportant des guerres</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-500 mt-0.5 flex-shrink-0">•</span>
                  <span>Plus l'écart est important, plus la victoire rapporte</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-500 mt-0.5 flex-shrink-0">•</span>
                  <span>Le taux de victoire reflète la performance globale</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-500 mt-0.5 flex-shrink-0">•</span>
                  <span>Défiez d'autres clubs pour grimper au classement</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Empty state */}
        {leaderboard.length === 0 && (
          <div className="text-center py-12 sm:py-16 px-4">
            <div className="text-5xl sm:text-6xl mb-4">🏆</div>
            <h3 className="text-lg sm:text-xl font-bold text-white mb-2">
              Aucun classement disponible
            </h3>
            <p className="text-sm sm:text-base text-gray-400">
              Les clubs doivent participer à des guerres pour apparaître ici
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default ClubsLeaderboard;